import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  Avatar,
  Badge,
  Button,
  Alert,
  Divider,
  ListItemIcon,
  Tooltip,
  Grid,
  Paper,
  Chip,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ThemeToggle from "./ThemeToggle";
// import { Brightness4, Brightness7 } from '@mui/icons-material';
import { MdOutlineBrightness5 } from "react-icons/md";
import { MdOutlineBrightness7 } from "react-icons/md";
import {
  FiChevronDown,
  FiLogOut,
  FiDatabase,
  FiBarChart2,
  FiTrendingUp,
  FiPieChart,
  FiFileText,
  FiClipboard,
  FiDollarSign,
  FiRadio,
  FiXCircle,
  FiRefreshCw,
  FiBriefcase,
  FiUsers,
  FiBarChart,
  FiShield,
  FiFolder,
  FiMenu,
  FiPackage,
  FiSettings,
} from "react-icons/fi";
import { useTheme as useThemeContext } from "../../../contexts/ThemeContext";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { becomeAdmin } from "../../../utils/restAPI.js";
import { isCurrentUserAdmin } from "../../../utils/authRoles";
import { useLogout } from "../../../hooks/useLogout";
import { toast } from "react-toastify";
import { requestMarketData } from "../../../Redux/Reducers/marketDataSlice";
import {
  updateLoadingMsg,
} from "../../../Redux/Reducers/uiSlice";
import { setShowMarketLoader } from "../../../Redux/Reducers/marketDataSlice";
import { getMarketDataThreshold } from "../../../Redux/Reducers/selectors";
import Popup from "../Popup";
import Sync from "../Topbar/Sync";
import Blacklist from "../Popup/Manager/Blacklist";
import TickerTape from "../../TickerTape/TickerTape";
import { sendWebSocketMessage } from "../../../hooks/useWebSocket";
import { selectLots, selectStrategies, selectManagers, selectNotifications, selectAccountingData, selectIsStrategist, selectIsInvestor, selectLivePrices, selectTrades } from "../../../Redux/Reducers/selectors";
import AddeparInventoryBadge from "../../../Pages/AddeparInventory/AddeparInventoryBadge";

const appVersion = process.env.REACT_APP_VERSION || "0.1.0";

function distinctStrategyManagerOwners(strategies) {
  if (!Array.isArray(strategies)) return 0;
  const set = new Set();
  for (const s of strategies) {
    const o = s?.owner;
    if (typeof o === "string" && o.startsWith("manager#")) set.add(o);
  }
  return set.size;
}

const TopNavigation = () => {
  const theme = useTheme();
  const { isDarkMode, toggleTheme } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSync, setIsSync] = useState(false);
  const [isBlacklist, setIsBlacklist] = useState(false);
  // Modal shown when a manager tries to become admin without 2FA enabled.
  const [mfaPromptOpen, setMfaPromptOpen] = useState(false);
  const [count, setCount] = useState(0);
  const { user, userInfo, apiInfo } = useSelector((state) => state?.users);
  const livePrices = useSelector(selectLivePrices);
  const isStrategist = useSelector(selectIsStrategist);
  const isInvestor = useSelector(selectIsInvestor);
  const accountingData = useSelector(selectAccountingData);
  const notifications = useSelector(selectNotifications);
  const managers = useSelector(selectManagers);
  const lots = useSelector(selectLots);
  const trades = useSelector(selectTrades);
  const strategies = useSelector(selectStrategies);
  const marketDataThreshold = useSelector(getMarketDataThreshold);
  const logout = useLogout();

  const nManagersFromState = Array.isArray(managers) ? managers.length : 0;
  const nManagersFromStrategies = distinctStrategyManagerOwners(strategies);
  const managersCount = isStrategist
    ? Math.max(nManagersFromState, nManagersFromStrategies)
    : nManagersFromState;

  const isAdmin = isCurrentUserAdmin();
  const isManager = !isStrategist && !isInvestor && managers?.length > 0;

  const profileSecondaryLabel = isAdmin
    ? "Admin"
    : managersCount > 1
      ? "Super Manager"
      : isStrategist
        ? "Strategist"
        : managers[0]?.name || "User";

  useEffect(() => {
    if (notifications && Array.isArray(notifications)) {
      const unreadCount = notifications.filter(
        (notification) => !notification?.isRead
      ).length;
      setCount(unreadCount);
    } else {
      setCount(0);
    }
  }, [notifications]);

  // Navigation items with icons and descriptions
  const getNavigationItems = () => {
    const items = [];
    if (isStrategist) {
      items.push(
        {
          title: "Performance",
          path: "/portfolio",
          icon: FiTrendingUp,
          description: "View portfolio performance metrics and analytics",
          color: "#4caf50",
          children: []
        },
        {
          title: "Strategies",
          path: "/strategies",
          icon: FiBarChart,
          description: "Manage and monitor trading strategies",
          color: "#2196f3",
          children: []
        },
        {
          title: "Signals",
          path: "/signals",
          icon: FiRadio,
          description: "View and manage trading signals",
          color: "#ff9800",
          children: [],
        },

        {
          title: "Strategy Performance",
          path: "/strategy-performance",
          icon: FiTrendingUp,
          description: "Analyze strategy performance metrics",
          color: "#e91e63",
          children: [],
        },
      );
    } else {
      items.push(
        {
          title: "Performance Dashboard",
          path: "/vantage",
          icon: FiPieChart,
          description: "Comprehensive performance dashboard",
          color: "#4caf50",
          children: [],
        },
        {
          title: "Portfolio",
          path: "/portfolio",
          icon: FiDatabase,
          description: "Portfolio overview and holdings",
          color: "#2196f3",
          children: [],
        },
        {
          title: "Positions",
          path: "/positions",
          icon: FiFolder,
          description: "Current positions and exposure",
          color: "#00acc1",
          children: [],
        },
        {
          title: "Trade Blotter",
          path: "/tradesV2",
          icon: FiClipboard,
          description: "View and manage all trade executions",
          color: "#f44336",
          children: [],
        },
        {
          title: "Rebalancer",
          path: "/rebalancer",
          icon: FiRefreshCw,
          description: "Rebalance portfolios and positions",
          color: "#607d8b",
          children: [],
        },
        {
          title: "Signals",
          path: "/signals",
          icon: FiRadio,
          description: "View and manage trading signals",
          color: "#ff9800",
          children: [],
        },
        {
          title: "EOD Reports",
          path: "/reports",
          icon: FiFileText,
          description: "End of day reports and statements",
          color: "#9c27b0",
          children: [],
        },
        {
          title: "Strategy Performance",
          path: "/strategy-performance",
          icon: FiTrendingUp,
          description: "Analyze strategy performance metrics",
          color: "#e91e63",
          children: [],
        },
        {
          title: "Fund Allocator",
          path: "/fundAllocator",
          icon: FiDollarSign,
          description: "Manage fund allocations and strategies",
          color: "#00bcd4",
          children: [],
        },
        {
          title: "Blacklist",
          path: "/funds-blacklist",
          icon: FiXCircle,
          description: "Manage blacklisted securities and funds",
          color: "#795548",
          children: [],
        },
        {
          title: "Entities",
          path: "/entities",
          icon: FiBriefcase,
          description: "Manage system entities and configurations",
          color: "#3f51b5",
          children: [
            { title: "Accounting", path: "/accounting", icon: FiFileText, description: "Accounting data and reconciliation", color: "#3f51b5" },
            { title: "Addepar Inventory", path: "/addepar-inventory", icon: FiClipboard, description: "Review and import Addepar entities", color: "#3f51b5" },
            { title: "Brokers", path: "/broker", icon: FiUsers, description: "Manage broker relationships", color: "#3f51b5" },
            { title: "Custodians", path: "/custodian", icon: FiDollarSign, description: "Custodian account management", color: "#3f51b5" },
            { title: "Funds", path: "/funds", icon: FiPieChart, description: "Fund management and configuration", color: "#3f51b5" },
            { title: "Strategies", path: "/strategies", icon: FiBarChart, description: "Strategy management and monitoring", color: "#3f51b5" },
            { title: "Security Master", path: "/security", icon: FiShield, description: "Security master data management", color: "#3f51b5" },
            { title: "Boxed Positions", path: "/boxed-positions", icon: FiPackage, description: "View boxed positions across funds", color: "#3f51b5" },
            { title: "Performance Dashboard", path: "/vantage-v2", icon: FiPieChart, description: "Modern performance dashboard", color: "#4caf50", badge: "New" },
            { title: "Portfolio", path: "/portfolio-v2", icon: FiDatabase, description: "Modern portfolio overview and holdings", color: "#2196f3", badge: "New" },
            // { title: "Store", path: "/store", icon: FiShield, description: "Strategies Store", color: "#3f51b5" },

          ],
        }
      );
    }
    return items;
  };

  const navigationItems = getNavigationItems();
  const [openMenus, setOpenMenus] = useState({});
  const [megaMenuAnchor, setMegaMenuAnchor] = useState(null);

  // Flatten navigation items to include all children, except Entities
  const getAllMenuItems = () => {
    const allItems = [];
    navigationItems.forEach((item) => {
      if (item.title === "Entities") {
        // Skip Entities, we'll handle it separately
        return;
      }
      if (item.children.length === 0) {
        // Add parent item if it has no children
        allItems.push(item);
      } else {
        // Add all children items instead of parent
        item.children.forEach((child) => {
          allItems.push({
            title: child.title,
            path: child.path,
            icon: child.icon || item.icon,
            description: child.description || item.description,
            color: child.color || item.color,
            children: [],
          });
        });
      }
    });
    return allItems;
  };

  const allMenuItems = getAllMenuItems();

  // Get Entities item separately
  const entitiesItem = navigationItems.find(item => item.title === "Entities");

  const handleMenuOpen = (event, itemTitle) => {
    setOpenMenus({ ...openMenus, [itemTitle]: event.currentTarget });
  };

  const handleMenuClose = (itemTitle) => {
    setOpenMenus({ ...openMenus, [itemTitle]: null });
  };

  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleMegaMenuOpen = (event) => {
    setMegaMenuAnchor(event.currentTarget);
  };

  const handleMegaMenuClose = () => {
    blurActiveElement();
    setMegaMenuAnchor(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    // Close all menus
    setOpenMenus({});
  };

  const handleMegaMenuNavigate = (path) => {
    // Drop focus + close menu, then tell heavy pages (Trade Blotter) to detach
    // their DataGrid before we change routes.
    blurActiveElement();
    setMegaMenuAnchor(null);
    setOpenMenus({});
    window.dispatchEvent(new CustomEvent("va:before-navigate", { detail: { path } }));
    navigate(path);
  };

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    blurActiveElement();
    setAnchorEl(null);
  };

  const handleBlacklist = () => {
    handleUserMenuClose();
    setIsBlacklist(true);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
  };

  const handleBecomeAdmin = async () => {
    handleUserMenuClose();
    const res = await becomeAdmin();
    if (res?.success) {
      toast.success(
        "You are now an admin. You'll be logged out in 5 seconds to apply your new privileges."
      );
      setTimeout(() => {
        handleLogout();
      }, 5000);
    } else if (res?.code === "MFA_REQUIRED") {
      setMfaPromptOpen(true);
    } else {
      toast.error(res?.message || "Failed to become admin. Please try again.");
    }
  };

  const handleMfaPromptContinue = () => {
    setMfaPromptOpen(false);
    navigate("/settings/security/two-factor");
  };

  const handleMfaPromptCancel = () => {
    setMfaPromptOpen(false);
  };

  const handleSettings = () => {
    handleUserMenuClose();
    navigate("/settings");
  };

  const handleNotification = () => {
    sendWebSocketMessage(`{"action":"modify_notifications"}`);
    navigate("/notifications");
  };

  const handleMarketData = () => {
    dispatch(updateLoadingMsg("Fetching Market Data"));
    dispatch(setShowMarketLoader(true));
    dispatch(
      requestMarketData({
        lots: lots,
        trades: trades,
        threshold: marketDataThreshold,
        market: "fmp",
      })
    );

    const timeoutDuration = livePrices?.length > 0 ? 30000 : 60000;
    setTimeout(() => {
      dispatch(updateLoadingMsg("Please Wait..."));
      dispatch(setShowMarketLoader(false));
    }, timeoutDuration);
  };
  const getUserAvatar = () => {
    const initials =
      (user?.firstName?.charAt(0) || "") +
      (user?.lastName?.charAt(0) || "");

    return (
      <Avatar
        alt="user-initials"
        sx={{
          border: `2px solid ${theme.palette.primary.main}`,
          cursor: "pointer",
          width: "42px",
          height: "42px",
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          fontWeight: 600,
          fontSize: "0.875rem",
          boxShadow: theme.shadows[2],
        }}
      >
        {initials || "U"}
      </Avatar>
    );
  };

  const formatSyncDate = (timestamp) => {
    if (!timestamp) return "Never";
    const date = new Date(parseInt(timestamp));
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-US", options).replace(/\//g, "-");
  };

  const getRelativeTime = (timestamp, refreshKey) => {
    void refreshKey; // when this changes, callers re-render so "Xm ago" stays current
    if (!timestamp) return "Never synced";

    const now = new Date();
    const syncDate = new Date(parseInt(timestamp));
    const diffMs = now - syncDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes} Minute${diffMinutes === 1 ? "" : "s"} Ago`;
    if (diffHours < 24) return `${diffHours} Hour${diffHours === 1 ? "" : "s"} Ago`;
    if (diffDays < 7) return `${diffDays} Day${diffDays === 1 ? "" : "s"} Ago`;
    if (diffWeeks < 4) return `${diffWeeks} Week${diffWeeks === 1 ? "" : "s"} Ago`;
    if (diffMonths < 12) return `${diffMonths} Month${diffMonths === 1 ? "" : "s"} Ago`;
    return `${diffYears} Year${diffYears === 1 ? "" : "s"} Ago`;
  };

  const isSyncRecent = (timestamp) => {
    if (!timestamp) return false;
    const syncDate = new Date(parseInt(timestamp));
    const now = new Date();
    const diffHours = (now - syncDate) / (1000 * 60 * 60);

    // If today is Monday, allow up to 72 hours
    if (now.getDay() === 1) {
      // Monday is 1
      return diffHours <= 60;
    }
    // Otherwise, allow up to 24 hours
    return diffHours <= 24;
  };

  // Sync stage mappings
  const ManagerSyncStage = {
    Completed: 0,
    Processing: 1,
    Started: 2
  };

  const AccountingSyncStage = {
    Completed: 0,
    Downloading: 1,
    Processing: 2,
    Started: 3
  };

  const getStageText = (stage, isRecent, isAccounting = false) => {
    if (isAccounting) {
      switch (stage) {
        case AccountingSyncStage.Completed: return isRecent ? "✓" : "⚠";
        case AccountingSyncStage.Downloading: return "↓";
        case AccountingSyncStage.Processing: return "⚙";
        case AccountingSyncStage.Started: return "▶";
        default: return "?";
      }
    } else {
      switch (stage) {
        case ManagerSyncStage.Completed: return isRecent ? "✓" : "⚠";
        case ManagerSyncStage.Processing: return "⚙";
        case ManagerSyncStage.Started: return "▶";
        default: return "?";
      }
    }
  };

  const getStageColor = (stage, isRecent, isAccounting = false) => {
    if (!isRecent) return "#ff9800"; // Orange for old sync

    if (isAccounting) {
      switch (stage) {
        case AccountingSyncStage.Completed: return "#4caf50"; // Green
        case AccountingSyncStage.Downloading: return "#2196f3"; // Blue
        case AccountingSyncStage.Processing: return "#ff9800"; // Orange
        case AccountingSyncStage.Started: return "#9c27b0"; // Purple
        default: return "#f44336"; // Red
      }
    } else {
      switch (stage) {
        case ManagerSyncStage.Completed: return "#4caf50"; // Green
        case ManagerSyncStage.Processing: return "#ff9800"; // Orange
        case ManagerSyncStage.Started: return "#9c27b0"; // Purple
        default: return "#f44336"; // Red
      }
    }
  };

  const CompactSyncStatus = () => {
    const seamlessSync = accountingData?.[0]?.sync;
    const addeparSync = accountingData?.[0]?.addeparSync;
    const [relativeTimeTick, setRelativeTimeTick] = useState(0);

    useEffect(() => {
      if (!seamlessSync?.date && !addeparSync?.date) return undefined;
      const id = setInterval(() => setRelativeTimeTick((n) => n + 1), 30000);
      return () => clearInterval(id);
    }, [seamlessSync?.date, addeparSync?.date]);

    const seamlessRecent = isSyncRecent(seamlessSync?.date);
    const addeparRecent = isSyncRecent(addeparSync?.date);
    const seamlessInProgress =
      seamlessSync != null &&
      seamlessSync.stage !== AccountingSyncStage.Completed;
    const addeparInProgress =
      addeparSync != null &&
      addeparSync.stage !== AccountingSyncStage.Completed;
    const bothCompleted = !seamlessInProgress && !addeparInProgress;
    const accountingInProgress = seamlessInProgress || addeparInProgress;
    // Prefer whichever sync is still running (don't use Seamless completed=0 over Addepar started)
    const activeStage = seamlessInProgress
      ? seamlessSync.stage
      : addeparInProgress
        ? addeparSync.stage
        : AccountingSyncStage.Completed;
    const accountingRecent =
      bothCompleted && seamlessRecent && (addeparSync == null || addeparRecent);
    const accountingStageColor = getStageColor(
      activeStage,
      bothCompleted ? accountingRecent : true,
      true
    );
    const accountingStageText = getStageText(
      activeStage,
      bothCompleted ? accountingRecent : true,
      true
    );

    const renderSyncStatusIcon = (sync, isRecent, inProgress) => {
      if (inProgress) {
        return (
          <CircularProgress
            size={14}
            thickness={5}
            sx={{ color: "rgba(255,255,255,0.95)", flexShrink: 0 }}
          />
        );
      }
      if (sync?.stage === AccountingSyncStage.Completed && isRecent) {
        return (
          <CheckCircleIcon
            sx={{ fontSize: 16, color: "rgba(255,255,255,0.95)", flexShrink: 0 }}
          />
        );
      }
      return (
        <WarningAmberIcon
          sx={{ fontSize: 16, color: "rgba(255,255,255,0.95)", flexShrink: 0 }}
        />
      );
    };

    const SyncHoverRow = ({ label, sync, isRecent, inProgress }) => (
      <Box sx={{ mb: label === "Seamless" ? 1.25 : 0 }}>
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "rgba(255,255,255,0.95)",
            lineHeight: 1.3,
            mb: 0.35,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {renderSyncStatusIcon(sync, isRecent, inProgress)}
          <Typography
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 400,
              color: "rgba(255,255,255,0.72)",
              lineHeight: 1.2,
            }}
          >
            {getRelativeTime(sync?.date, relativeTimeTick)}
          </Typography>
        </Box>
      </Box>
    );

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 1.5,
          backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          minWidth: "fit-content",
        }}
      >
        <Tooltip
          title={
            <Box sx={{ py: 0.5, px: 0.25, minWidth: 148 }}>
              <SyncHoverRow
                label="Seamless"
                sync={seamlessSync}
                isRecent={seamlessRecent}
                inProgress={seamlessInProgress}
              />
              <SyncHoverRow
                label="Addepar"
                sync={addeparSync}
                isRecent={addeparRecent}
                inProgress={addeparInProgress}
              />
            </Box>
          }
          arrow
          placement="bottom"
          enterTouchDelay={0}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: accountingStageColor,
                color: "white",
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
              },
            },
            arrow: {
              sx: {
                color: accountingStageColor,
              },
            },
          }}
        >
          <Box
            component="span"
            tabIndex={0}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              outline: "none",
              "&:focus-visible": {
                boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}`,
              },
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: accountingStageColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                color: "white",
                fontWeight: "bold",
                lineHeight: 1,
                textAlign: "center",
                flexShrink: 0,
              }}
            >
              {accountingInProgress ? (
                <CircularProgress
                  size={16}
                  thickness={5}
                  sx={{ color: "white" }}
                />
              ) : (
                accountingStageText
              )}
            </Box>
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontSize: "14px",
                color: theme.palette.text.secondary,
                fontWeight: 600,
              }}
            >
              Accounting Sync
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      {/* Top Navigation Bar */}
      <Box
        className="top-navigation"
        sx={{
          width: "100%",
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 1,
            minHeight: "64px",
          }}
        >
          {/* Logo and Navigation Items */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Logo */}
            <Box sx={{ display: "flex", alignItems: "baseline" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: "24px",
                  color: theme.palette.primary.main,
                  textTransform: "uppercase",
                }}
              >
                Vantage
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "300",
                  fontSize: "24px",
                  color: theme.palette.primary.main,
                  ml: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Alpha
              </Typography>
              <Typography
                component="span"
                sx={{
                  ml: 0.75,
                  fontSize: "13px",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  color: theme.palette.text.secondary,
                  opacity: 0.8,
                  lineHeight: 1,
                  userSelect: "none",
                }}
              >
                v{appVersion}
              </Typography>
            </Box>

            {/* Hamburger Menu Icon */}
            <IconButton
              onClick={handleMegaMenuOpen}
              onMouseEnter={handleMegaMenuOpen}
              sx={{
                color: theme.palette.text.primary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.primary.main,
                },
              }}
            >
              <FiMenu />
            </IconButton>
          </Box>

          {/* Mega Menu - Shows all navigation items in grid */}
          <Menu
            anchorEl={megaMenuAnchor}
            open={Boolean(megaMenuAnchor)}
            onClose={handleMegaMenuClose}
            onMouseLeave={handleMegaMenuClose}
            disableAutoFocusItem
            disableRestoreFocus
            closeAfterTransition={false}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 900,
                maxWidth: 1000,
                boxShadow: theme.shadows[8],
                borderRadius: 2,
                p: 3,
                backgroundColor: theme.palette.background.paper,
              },
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            MenuListProps={{
              onMouseLeave: handleMegaMenuClose,
            }}
          >
            <Box sx={{ display: "flex", gap: 3 }}>
              {/* Regular menu items in cards */}
              <Box sx={{ flex: 1 }}>
                <Grid container spacing={1.5}>
                  {allMenuItems.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <Grid size={{ xs: 6 }} key={item.title}>
                        <Paper
                          onClick={() => {
                            handleMegaMenuNavigate(item.path);
                          }}
                          sx={{
                            p: 1.5,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                            height: "100%",
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                              transform: "translateY(-2px)",
                              boxShadow: theme.shadows[4],
                              borderColor: item.color || theme.palette.primary.main,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "flex-start",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1.5,
                                background: item.color
                                  ? `linear-gradient(135deg, ${item.color} 0%, ${item.color}dd 100%)`
                                  : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <ItemIcon
                                style={{
                                  color: "white",
                                  fontSize: 18,
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  mb: 0.25,
                                  fontSize: "0.9rem",
                                }}
                              >
                                {item.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: "0.75rem",
                                  lineHeight: 1.4,
                                }}
                              >
                                {item.description || "Navigate to this section"}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {entitiesItem && (
                <>
                  {/* Divider */}
                  <Divider orientation="vertical" flexItem />

                  {/* Entities column - simple list */}
                  <Box sx={{ minWidth: 200 }}>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          mb: 1.5,
                          fontSize: "1rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {entitiesItem.title}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {entitiesItem.children.map((child) => {
                          return (
                            <Box
                              key={`${child.title}-${child.path}`}
                              onClick={() => {
                                handleMegaMenuNavigate(child.path);
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                cursor: "pointer",
                                color: theme.palette.text.primary,
                                fontSize: "0.875rem",
                                lineHeight: 1.6,
                                transition: "background-color 0.2s ease",
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  color: "inherit",
                                  fontSize: "inherit",
                                  lineHeight: "inherit",
                                }}
                              >
                                {child.title}
                              </Typography>
                              {child.badge && (
                                <Chip
                                  label={child.badge}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontWeight: 800,
                                    fontSize: "0.62rem",
                                    letterSpacing: "0.04em",
                                    textTransform: "uppercase",
                                    color: "#fff",
                                    backgroundColor: "#E53935",
                                    "& .MuiChip-label": { px: 0.7 },
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Menu>

          {/* Right Side - Notifications, User Menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Sync Status + Addepar pending badge */}
            {!isStrategist && !isInvestor && managers?.length > 0 && (
              <>
                <AddeparInventoryBadge />

                <CompactSyncStatus />
              </>
            )}

            {/* Market Data */}
            <Tooltip title="Fetch Market Data" arrow placement="bottom">
              <IconButton
                onClick={handleMarketData}
                sx={{ color: theme.palette.text.primary }}
              >
                <FiBarChart2 />
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            {/* {!isStrategist && !isInvestor && (
            <IconButton
              onClick={handleNotification}
              sx={{ color: theme.palette.text.primary }}
            >
              <Badge badgeContent={count} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          )} */}

            {/* User Menu */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                cursor: "pointer",
                px: 2,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: Boolean(anchorEl)
                  ? theme.palette.action.selected
                  : theme.palette.background.paper,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                  boxShadow: theme.shadows[2],
                },
              }}
              onClick={handleUserMenuClick}
            >
              {getUserAvatar()}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    lineHeight: 1.2,
                    fontSize: "0.875rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "150px",
                  }}
                >
                  {`${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "User"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "150px",
                  }}
                >
                  {profileSecondaryLabel}
                </Typography>
              </Box>
              <FiChevronDown
                style={{
                  fontSize: 18,
                  color: theme.palette.text.secondary,
                  transition: "transform 0.2s ease",
                  transform: Boolean(anchorEl) ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </Box>

            {/* User Dropdown Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              disableAutoFocusItem
              disableRestoreFocus
              closeAfterTransition={false}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  boxShadow: theme.shadows[8],
                  borderRadius: 2,
                },
              }}
            >
              <MenuItem onClick={handleSettings} sx={{  }}>
                <ListItemIcon sx={{ zoom: 0.75 }}>
                  <FiSettings style={{ fontSize: '15px' }} />
                </ListItemIcon>
                Settings
              </MenuItem>

              {/* Theme Toggle Menu Item */}

              <MenuItem onClick={(e) => {
                e.stopPropagation();
                toggleTheme();
                handleUserMenuClose();
              }} sx={{}}>
                <ListItemIcon sx={{ zoom: 0.75 }}>
                  {isDarkMode ? <MdOutlineBrightness7 style={{ fontSize: '15px' }} /> : <MdOutlineBrightness5 style={{ fontSize: '15px' }} />}
                </ListItemIcon>
                {isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              </MenuItem>

              {!isAdmin && isManager && (
                <MenuItem onClick={handleBecomeAdmin} sx={{}}>
                  <ListItemIcon sx={{ zoom: 0.75 }}>
                    <FiShield style={{ fontSize: '15px' }} />
                  </ListItemIcon>
                  Become Admin
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout} sx={{ color: "error.main", }}>
                <ListItemIcon sx={{ zoom: 0.75 }}>
                  <FiLogOut style={{ color: theme.palette.error.main, fontSize: '15px' }} />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      <Box className="ticker-tape-shell">
        <Box className="ticker-tape-shell__inner">
          <TickerTape />
        </Box>
      </Box>

      {/* Popups */}
      <Popup
        title="SYNC DATA ITEMS"
        openPopup={isSync}
        setOpenPopup={setIsSync}
      >
        <Sync setIsSync={setIsSync} />
      </Popup>
      <Blacklist
        managers={managers}
        strategies={strategies}
        openBlacklistPopup={isBlacklist}
        setBlacklistOpenPopup={setIsBlacklist}
        title="Manager Blacklist"
      />

      {/* Prompt shown when a manager tries to become admin without 2FA. */}
      <Dialog
        open={mfaPromptOpen}
        onClose={handleMfaPromptCancel}
        maxWidth="xs"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              // Dim + blur the background so the modal is the user's focus.
              backgroundColor: alpha("#0f172a", 0.55),
              backdropFilter: "blur(4px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            // Soft neutral shadow (no colored edge glow).
            boxShadow: "0 16px 48px rgba(0, 0, 0, 0.28)",
          },
        }}
      >
        {/* Header band — same gradient as the app's contained buttons. */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            pt: 4,
            pb: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: alpha("#ffffff", 0.18),
              border: `1px solid ${alpha("#ffffff", 0.3)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <FiShield style={{ fontSize: 34, color: "#ffffff" }} />
          </Box>
          <Typography
            variant="h6"
            sx={{ color: "#ffffff", fontWeight: 700, letterSpacing: 0.2 }}
          >
            Two-Factor Authentication
          </Typography>
        </Box>

        <DialogContent sx={{ textAlign: "center", px: 4, pt: 3 }}>
          <DialogContentText
            sx={{ fontSize: "0.95rem", lineHeight: 1.6, color: "text.secondary" }}
          >
            To become an admin you first need to enable{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              two-factor authentication
            </Box>
            . If you continue, we'll take you to the setup screen to scan a QR
            code with your authenticator app. Once enabled, just try{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              “Become Admin”
            </Box>{" "}
            again.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 4, pt: 1, gap: 1.5 }}>
          <Button
            onClick={handleMfaPromptCancel}
            fullWidth
            sx={{
              textTransform: "none",
              borderRadius: 2.5,
              py: 1.1,
              fontWeight: 600,
              color: "text.secondary",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMfaPromptContinue}
            fullWidth
            color="primary"
            variant="contained"
            autoFocus
            sx={{
              textTransform: "none",
              borderRadius: 2.5,
              py: 1.1,
              fontWeight: 700,
              boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.2)}`,
              "&:hover": {
                boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.28)}`,
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TopNavigation;
